import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { CaseDetail, ModelPrediction } from '../api/types'
import {
  buildPredictionView,
  correctness,
  VERDICT_LABEL,
} from '../lib/predict'
import { useViewMode } from './ModeProvider'
import { Term } from './Term'
import { CorrectnessBadge } from './CorrectnessBadge'

interface Props {
  detail: CaseDetail
}

const TRUTH_LABEL: Record<string, string> = {
  Malignant: 'Kötü huylu',
  Benign: 'İyi huylu',
  Unknown: 'Bilinmiyor',
}

const MODE_LABEL: Record<string, string> = {
  val_fold: 'tek model, vakayı eğitimde görmedi',
  ensemble_cross_cohort: '5 model, kohort dışı topluluk',
  ensemble_no_fold: '5 model topluluğu',
}

export function ClassifierPanel({ detail }: Props) {
  const { isAdvanced } = useViewMode()
  const view = buildPredictionView(detail)
  const correct = correctness(detail, view)
  const truthCls = detail.label === 'Malignant' ? 'text-malignant' : 'text-benign'
  const showTruth = detail.label !== 'Unknown'

  const verdictText = VERDICT_LABEL[view.verdict]
  const verdictCls =
    view.verdict === 'malignant' ? 'text-malignant'
    : view.verdict === 'benign' ? 'text-benign'
    : 'text-warn'
  const confidencePct = Math.round(view.confidence * 100)

  return (
    <div className="border border-border rounded bg-surface p-4">
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <h2 className="text-text font-medium">Sınıflandırma sonucu</h2>
        {showTruth && correct !== 'unknown' && (
          <CorrectnessBadge correctness={correct} size="md" />
        )}
      </div>

      <div className="mb-3">
        <div className={`text-lg sm:text-xl font-semibold ${verdictCls}`}>
          {verdictText}
        </div>
        <div className="text-xs text-text-dim mt-0.5">
          {view.verdict === 'unsure' ? (
            <>
              Model net bir karara varamadı. Tahmin %50'ye çok yakın olduğu için
              iki taraftan biri seçilmedi.
            </>
          ) : (
            <>
              Model bu tahminden %{confidencePct} oranında emin.
            </>
          )}
        </div>
        <ConfidenceBar verdict={view.verdict} confidence={view.confidence} />
      </div>

      {showTruth && (
        <div className="text-xs text-text-dim mb-3 flex items-baseline gap-2 flex-wrap">
          <span>Gerçek etiket:</span>
          <span className={`text-sm font-medium ${truthCls}`}>
            {TRUTH_LABEL[detail.label]}
          </span>
          {correct === 'borderline' && (
            <span className="text-[11px] text-text-dim italic">
              Model kararsız kaldığı için doğru/yanlış denmiyor.
            </span>
          )}
          {correct === 'wrong' && view.verdict !== 'unsure' && (
            <span className="text-[11px] text-text-dim italic">
              Modelin tahmini gerçek etiketten farklı.
            </span>
          )}
        </div>
      )}

      {isAdvanced ? (
        <AdvancedDetails detail={detail} />
      ) : (
        <p className="text-[11px] text-text-dim border-t border-border pt-3 leading-relaxed">
          Bu özet, vakanın ait olduğu kohorta uygun model tarafından üretilir.
          Modellerin ayrı tahminlerini ve teknik ayrıntıları görmek için
          sağ üstten <span className="font-medium text-text">İleri</span>{' '}
          görünümüne geçebilirsiniz.
        </p>
      )}
    </div>
  )
}

function ConfidenceBar({
  verdict,
  confidence,
}: {
  verdict: 'malignant' | 'benign' | 'unsure'
  confidence: number
}) {
  const pct = Math.round(confidence * 100)
  const fillCls =
    verdict === 'malignant' ? 'bg-malignant/50'
    : verdict === 'benign' ? 'bg-benign/50'
    : 'bg-warn/40'
  return (
    <div className="mt-2">
      <div className="flex justify-between text-[10px] text-text-dim mb-1">
        <span>Kararsız</span>
        <span>Tam emin</span>
      </div>
      <div className="bg-surface-2 rounded h-2 overflow-hidden">
        <div className={`${fillCls} h-full`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

function AdvancedDetails({ detail }: { detail: CaseDetail }) {
  const [expanded, setExpanded] = useState(true)
  const v4 = detail.predictions.v4v2_ambl_only
  const v16 = detail.predictions.v16_combined
  return (
    <div className="border-t border-border pt-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-xs text-text-dim hover:text-text flex items-center gap-1"
      >
        <span>{expanded ? '▾' : '▸'}</span>
        <span>Model detayları</span>
      </button>
      {expanded && (
        <div className="mt-3 space-y-4">
          <ModelRow
            name="AMBL modeli"
            deployment="Yalnızca AMBL üzerinde eğitilmiştir. AMBL'ye benzer çekimlerde en yüksek doğruluğu verir, farklı kaynaklarda doğrulanmamıştır."
            pred={v4}
            flagCrossCohort={detail.cohort === 'BD'}
          />
          <div className="border-t border-border" />
          <ModelRow
            name="Kombine model"
            deployment="AMBL ve BREAST-DIAGNOSIS birleştirilerek eğitilmiştir. Karışık kaynaklarda daha tutarlı, AMBL'de biraz daha düşük doğruluk verir."
            pred={v16}
            flagCrossCohort={false}
          />
          <div className="text-[11px] text-text-dim leading-relaxed">
            Olasılıklar, sınıflandırıcının dört farklı görüntü yansıması üzerinden
            ortalamasıdır (<Term id="tta">test zamanı artırma</Term>). Modelin eğitim
            sırasında o vakayı görüp görmediği ve kaç farklı eğitilmiş modelin
            tahmininin birleştirildiği üst satırda belirtilir. Sözlük için{' '}
            <Link to="/hakkinda#sozluk" className="text-accent">
              Hakkında sayfasına
            </Link>{' '}
            bakabilirsiniz.
          </div>
        </div>
      )}
    </div>
  )
}

function ModelRow({
  name,
  deployment,
  pred,
  flagCrossCohort,
}: {
  name: string
  deployment: string
  pred: ModelPrediction
  flagCrossCohort: boolean
}) {
  const pct = pred.p_malignant * 100
  const cls = pred.p_malignant >= 0.5 ? 'text-malignant' : 'text-benign'
  const verdict = pred.p_malignant >= 0.5 ? 'Kötü huylu' : 'İyi huylu'
  const modeLabel = MODE_LABEL[pred.mode] || pred.mode

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-1 gap-0 sm:gap-2">
        <span className="text-text font-medium text-sm">{name}</span>
        <span className="text-[11px] sm:text-xs text-text-dim sm:text-right">
          {modeLabel}
        </span>
      </div>
      <div className="text-[11px] text-text-dim mb-2">{deployment}</div>
      <div className="bg-surface-2 rounded h-6 overflow-hidden relative mb-1">
        <div
          className={pred.p_malignant >= 0.5 ? 'bg-malignant/40 h-full' : 'bg-benign/40 h-full'}
          style={{ width: `${pct}%` }}
        />
        <div className="absolute inset-0 flex items-center justify-between px-2 text-xs">
          <span className={`font-medium ${cls}`}>{verdict}</span>
          <span className="font-mono text-text">%{pct.toFixed(1)}</span>
        </div>
      </div>
      {pred.fold_probs.length > 1 && (
        <div className="text-[11px] text-text-dim font-mono">
          5 modelin tahminleri: [{pred.fold_probs.map((p) => p.toFixed(2)).join(', ')}]
        </div>
      )}
      {flagCrossCohort && (
        <div className="text-[11px] text-warn mt-1">
          Bu model yalnızca AMBL üzerinde eğitilmiştir. Farklı bir kohortta sonucun
          olasılık 0.5 civarına yakınsaması beklenebilir (<Term id="alan-kaymasi">alan
          kayması</Term>).
        </div>
      )}
    </div>
  )
}

